import {
  DataGrid as FluentDataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  createTableColumn,
} from '@fluentui/react-components';
import type { TableColumnDefinition, TableRowId } from '@fluentui/react-components';
import type { ReactNode } from 'react';

import { EmptyState } from './ScreenChrome';

/** Declarative column for the generic resource table. */
export interface DataColumn<T> {
  columnId: string;
  header: ReactNode;
  renderCell: (item: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<DataColumn<T>>;
  rows: readonly T[];
  /** Stable, unique id per row (React key + row id). */
  getRowId: (item: T) => string;
  emptyMessage: string;
}

/**
 * Generic Fluent DataGrid resource table for the admin screens (the WorkLogix
 * web-app DataTable pattern). Row actions render inside cells — rows are not
 * clickable, so a Switch or menu inside a cell never fights row activation.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  emptyMessage,
}: DataTableProps<T>): JSX.Element {
  if (rows.length === 0) {
    return <EmptyState message={emptyMessage} />;
  }

  const columnDefs: Array<TableColumnDefinition<T>> = columns.map((col) =>
    createTableColumn<T>({
      columnId: col.columnId,
      renderHeaderCell: () => col.header,
      renderCell: (item) => col.renderCell(item),
    })
  );

  const resolveRowId = (item: T): TableRowId => getRowId(item);

  return (
    <FluentDataGrid items={rows as T[]} columns={columnDefs} getRowId={resolveRowId} size="medium">
      <DataGridHeader>
        <DataGridRow>
          {({ renderHeaderCell }): JSX.Element => (
            <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
          )}
        </DataGridRow>
      </DataGridHeader>
      <DataGridBody<T>>
        {({ item, rowId }): JSX.Element => (
          <DataGridRow<T> key={rowId}>
            {({ renderCell }): JSX.Element => <DataGridCell>{renderCell(item)}</DataGridCell>}
          </DataGridRow>
        )}
      </DataGridBody>
    </FluentDataGrid>
  );
}
